import { useEffect, useState } from 'react';
import { graphql } from '@lib/api';
import GQL from '@lib/cms/gql';
import { gql } from '@apollo/client';

import Carousel from '@components/topic-carousel';
import ContactForm from '@components/forms';
import Container from '@layout/container';
import Icon from '@components/icon';
import Image from '@components/image';
import Jumbotron from '@components/jumbotron';
import Layout from '@layout/index';
import Link from '@components/link';
import PostList from '@components/post-list';
import PropertyCard from '@components/card';
import RealtorCard from '@components/realtor-card';
import SearchByAgent from '@components/agent-finder/search-by-agent/search-agents-single-office';
import SellWithSibcy from '@components/sell-with-sibcy';
import Grid from '@layout/grid';

import {
  OFFICE_PATHS_QUERY,
  OFFICE_DETAIL_QUERY,
  OFFICE_ATTRIBUTES,
  NEW_OFFICE_LISTINGS
} from '@lib/fragments';
import { toKebabCase, shuffleArrayInPlace, getSlug } from '@lib/utils';

import {
  manager_container,
  directions_label,
  left_column,
  office_image,
  new_listings_header
} from '../styles.module.scss';

export async function getStaticPaths() {
  const { data } = await GQL.fetch(OFFICE_PATHS_QUERY);

  const paths = data?.officeCollection?.items.map((item) => {
    const kebabSlug = toKebabCase(item.slug);
    return { params: { slug: kebabSlug } };
  });

  return {
    fallback: false,
    paths
  };
}

export async function getStaticProps(context) {
  const slug = context.params.slug;
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment || null;
  const { data: cmsFetchData } = await GQL.fetch(
    OFFICE_DETAIL_QUERY,
    { preview, slug },
    preview,
    previewEnv
  );
  const { officeId } = cmsFetchData.officeCollection.items[0];

  const data = await graphql(
    gql`
      ${OFFICE_ATTRIBUTES}
      query GetOfficeDetails($officeId: Int!) {
        officeSearch(where: { officeId: { eq: $officeId } }) {
          __typename
          items {
            ...Office
          }
        }
      }
    `,
    { officeId }
  );

  const officeData = data?.officeSearch.items[0];
  const cmsData = cmsFetchData?.officeCollection.items[0];

  if (!cmsData || !officeData) {
    return {
      notFound: true
    };
  }

  const managers = officeData.agents.filter((agent) => agent.isManager);
  const agents = officeData.agents.filter((agent) => !agent.isManager);
  shuffleArrayInPlace(agents);

  return {
    props: {
      agents,
      cmsData,
      managers,
      officeData,
      preview
    },
    revalidate: 300
  };
}

const OfficeDetailPage = ({ officeData, managers, agents, cmsData, preview }) => {
  const [newListings, setNewListings] = useState([]);

  const carouselSlides =
    cmsData.nearbyCommunitiesCollection?.items?.map((item) => ({
      description: item?.excerpt || item?.introductionText || item?.summaryText,
      featuredImage: item?.featuredImage || {
        height: 960,
        url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
        width: 1440
      },
      linkLabel: item?.linkLabel || 'Explore Community',
      linkUrl: item.slug && `/community/${item.slug}`,
      title: item?.title
    })) || [];

  useEffect(() => {
    const fetchNewListings = async () => {
      const newListingData = await graphql(
        gql`
          ${NEW_OFFICE_LISTINGS}
        `,
        { officeId: officeData.officeId, take: 3 }
      );

      setNewListings(newListingData?.listingSearch.items || []);
    };

    fetchNewListings();
  }, []);

  return (
    <Layout
      meta={{
        description: cmsData?.metaDescription || cmsData?.description,
        image: cmsData?.metaImage || { url: officeData?.photoUrl },
        title: cmsData?.title || officeData?.name
      }}
      preview={preview}
    >
      <Jumbotron
        theme="bar"
        image={{
          alt: cmsData?.headerImage?.alt,
          height: cmsData?.headerImage?.height,
          src: cmsData?.headerImage?.url || officeData.photoUrl,
          width: cmsData?.headerImage?.width
        }}
        body="Office"
        title={cmsData.title || officeData.name}
      />

      <Container>
        <section>
          <div className={left_column}>
            {officeData.photoUrl && (
              <Image
                className={office_image}
                image={{ alt: officeData.name, height: 272, src: officeData.photoUrl, width: 400 }}
              />
            )}
            <p>{cmsData.description}</p>
            <p>
              Phone: <a href={`tel:${officeData.phone}`}>{officeData.phone}</a> <br></br>
              Fax: <a href={`tel:${officeData.fax}`}>{officeData.fax}</a> <br></br>
            </p>
            <div>
              <Link url={officeData.geoLocationMapExternalLink}>
                {officeData.address} <br></br>
                {officeData.city}, {officeData.state} {officeData.zipCode} <br></br>
              </Link>
              <span className={directions_label}>
                <Link url={officeData.geoLocationMapExternalLink}>
                  Get Directions
                  <Icon name="small-right" type="arrow" />
                </Link>
              </span>
            </div>
          </div>
          <div className={manager_container}>
            {managers.map(({ agent }) => {
              return (
                <div key={agent.agentId}>
                  <RealtorCard
                    contact={{
                      displayPrimary: agent.displayPrimary,
                      displayPhone: agent.displayPhone,
                      displayEmail: agent.displayEmail,
                      displayWebsiteUrl: agent.displayWebsiteUrl,
                      displayPhotoUrl: agent.displayPhotoUrl,
                      professionalTitle: agent.professionalTitle,
                      languages: agent.languages
                    }}
                    theme="officeDetail"
                  />
                </div>
              );
            })}
          </div>
        </section>
        <ContactForm
          header="Contact Us"
          formDescription="Connect with an agent and learn more about our team, properties and any other real estate services!"
          linkLabel="Contact Us"
          theme="office"
          endpoint="office information request"
          endpointId={officeData.officeId}
        ></ContactForm>
      </Container>

      {officeData.officeId !== 123 && (
        <>
          {newListings.length > 0 && (
            <>
              <Container
                textAlignment="center"
                theme="background-is-gray"
                gridOptions={{ layout: 'single' }}
              >
                <div className={new_listings_header}>
                  <h2>Newest Homes for Sale</h2>
                  <p>
                    Check out the most recently listed properties from our{' '}
                    {cmsData.name || officeData.name} office.
                  </p>
                  <Link url={`/office/${cmsData.slug}/listings`}>
                    View All <Icon type="arrow" name={'small-right'} />
                  </Link>
                </div>
                <Grid layout="third">
                  {newListings.map((listing) => {
                    return (
                      <PropertyCard
                        image={{
                          src: !listing.mainPhoto
                            ? 'https://place-hold.it/340x231/green/white&text=FPO&fontsize=16'
                            : `${listing.mainPhoto.midSizeImageUrl}`
                        }}
                        property={{
                          ...listing,
                          details: [
                            { br: listing.bedroomCount },
                            {
                              ba: listing.partBathroomCount
                                ? `${listing.fullBathroomCount}/${listing.partBathroomCount}`
                                : listing.fullBathroomCount
                            },
                            { ac: listing.acres }
                          ],
                          price: listing.priceFormatted,
                          realtor: {
                            isActiveSibcyAgent: true,
                            primaryRealtorName: listing.primaryAgentDisplayOne,
                            secondaryRealtorName: listing.secondaryAgentDisplayOne
                          }
                        }}
                        url={
                          listing.canonicalUrl
                            ? getSlug(listing.canonicalUrl)
                            : `/listing/${listing.listingId}`
                        }
                        key={`card-${listing.listingId}`}
                      />
                    );
                  })}
                </Grid>
              </Container>
            </>
          )}
          {carouselSlides?.length > 0 && (
            <Carousel
              carouselId="custom-nearby-communities"
              carouselHeader="Explore Closeby Communities"
              carouselDescription="We grow and retain, durable, long-term relationships with all our vendor partners."
              theme="full-width"
              slidesCollection={{ cards: carouselSlides }}
            />
          )}

          <Container
            header="Agents"
            description="Feel free to contact any of the agents in our office directly, or connect with our office to be matched with an expert in your market."
            spacing="large"
            theme="background-gray-to-white"
            width="large"
            textAlignment="center"
          >
            <SearchByAgent agentData={agents} />
          </Container>
        </>
      )}

      <Container width="full" layout="Two thirds, one third" gutter="25">
        <SellWithSibcy />
        <PostList title="Must reads" />
      </Container>
    </Layout>
  );
};

export default OfficeDetailPage;
